import { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type ApiTokenRecord,
  createApiToken,
  listApiTokens,
  revokeApiToken,
} from "@/api/apiTokens";
import { listUsers } from "@/api/users";
import { PageHeader } from "@/components/PageHeader";

export function ApiTokensAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [userId, setUserId] = useState("");
  const [ttlDays, setTtlDays] = useState(365);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const tokens = useQuery({
    queryKey: ["api-tokens"],
    queryFn: listApiTokens,
  });

  const users = useQuery({
    queryKey: ["admin-users-active"],
    queryFn: () => listUsers({ status: "ACTIVE", limit: 200 }),
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: () => createApiToken(name, userId, ttlDays),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["api-tokens"] });
      setCreatedToken(r.token);
      setName("");
      setUserId("");
      setTtlDays(365);
      setFormError(null);
    },
    onError: (e: unknown) => {
      const d = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setFormError(d ?? (e as Error).message);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeApiToken(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-tokens"] }),
  });

  const copyToken = async () => {
    if (createdToken) {
      try {
        await navigator.clipboard.writeText(createdToken);
      } catch {
        // clipboard restricted contexts — silent
      }
    }
  };

  const closeDialog = () => {
    setOpen(false);
    setCreatedToken(null);
    setFormError(null);
  };

  return (
    <Box>
      <PageHeader
        helpTerm="apiToken"
        title={t("apiTokens.title")}
        subtitle={t("apiTokens.subtitle")}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setOpen(true);
              setCreatedToken(null);
            }}
          >
            {t("apiTokens.create")}
          </Button>
        }
      />

      {tokens.isLoading && <CircularProgress />}
      {tokens.data && tokens.data.length === 0 && (
        <Alert severity="info">{t("apiTokens.empty")}</Alert>
      )}
      {tokens.data && tokens.data.length > 0 && (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("apiTokens.name")}</TableCell>
                <TableCell>{t("apiTokens.user")}</TableCell>
                <TableCell>{t("apiTokens.expiresAt")}</TableCell>
                <TableCell>{t("apiTokens.lastUsed")}</TableCell>
                <TableCell>{t("apiTokens.status")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.data.map((tok: ApiTokenRecord) => (
                <TableRow key={tok.id}>
                  <TableCell>{tok.name}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>{tok.user_id.slice(0, 8)}…</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    {tok.expires_at.slice(0, 16).replace("T", " ")}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    {tok.last_used_at ? tok.last_used_at.slice(0, 16).replace("T", " ") : "—"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={tok.revoked_at ? t("apiTokens.revoked") : t("apiTokens.active")}
                      color={tok.revoked_at ? "default" : "success"}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {!tok.revoked_at && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          if (confirm(t("apiTokens.revokeConfirm"))) {
                            revokeMutation.mutate(tok.id);
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{t("apiTokens.createTitle")}</DialogTitle>
        <DialogContent>
          {!createdToken && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label={t("apiTokens.name")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                size="small"
                inputProps={{ maxLength: 120 }}
              />
              <FormControl size="small">
                <InputLabel>{t("apiTokens.user")}</InputLabel>
                <Select
                  label={t("apiTokens.user")}
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                >
                  {(users.data?.items ?? []).map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={t("apiTokens.ttlDays")}
                type="number"
                size="small"
                value={ttlDays}
                onChange={(e) => setTtlDays(Number(e.target.value))}
                inputProps={{ min: 1, max: 3650 }}
              />
              <Alert severity="warning">{t("apiTokens.warning")}</Alert>
              {formError && <Alert severity="error">{formError}</Alert>}
            </Stack>
          )}
          {createdToken && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="success">{t("apiTokens.createdNotice")}</Alert>
              <Typography variant="caption">{t("apiTokens.tokenLabel")}</Typography>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "background.default",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  fontFamily: "monospace",
                  fontSize: 12,
                  wordBreak: "break-all",
                }}
              >
                {createdToken}
              </Box>
              <Button startIcon={<ContentCopyIcon />} onClick={copyToken}>
                {t("apiTokens.copy")}
              </Button>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>
            {createdToken ? t("common.close") : t("common.cancel")}
          </Button>
          {!createdToken && (
            <Button
              variant="contained"
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending || !name || !userId}
            >
              {t("common.save")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
